import type { CaptionTextItemTemplate } from '@/features/media-library/utils/caption-items'

/**
 * 字幕样式映射表
 *
 * clip-config 页的字幕样式选择器用 `captionLibraryStyles` 里的 key（如 'blue'、
 * 'deepdiver'）作为预览资源标识，但代码库里没有 key → 字体/颜色 的客户端映射。
 * 这里为常见 key 定义 CaptionTextItemTemplate，未命中的 key 回退到
 * DEFAULT_CLIP_CAPTION_STYLE。
 *
 * 模板字段会被 buildCaptionTextItems 通过 `{ ...defaultCaptionItem, ...styleTemplate }`
 * 合并到默认字幕 item 上，所以只需给出要覆盖的字段。
 */

/** 默认字幕样式：白字 + 半透明黑底 + 投影 */
export const DEFAULT_CLIP_CAPTION_STYLE: CaptionTextItemTemplate = {
  fontSize: 52,
  fontFamily: 'Inter',
  fontWeight: 'semibold',
  color: '#ffffff',
  backgroundColor: 'rgba(0, 0, 0, 0.55)',
  textAlign: 'center',
  verticalAlign: 'middle',
  lineHeight: 1.15,
  letterSpacing: 0,
  textShadow: {
    offsetX: 0,
    offsetY: 3,
    blur: 10,
    color: 'rgba(0, 0, 0, 0.75)',
  },
}

/** 为常见字幕样式 key 定义的视觉模板（只列出要覆盖默认值的字段） */
const partialTemplates: Record<string, Partial<CaptionTextItemTemplate>> = {
  blue: {
    color: '#3B82F6',
    fontWeight: 'bold',
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    stroke: { width: 2, color: '#000000' },
    textShadow: { offsetX: 0, offsetY: 2, blur: 8, color: 'rgba(0, 0, 0, 0.8)' },
  },
  deepdiver: {
    color: '#00E5FF',
    fontWeight: 'bold',
    backgroundColor: 'transparent',
    stroke: { width: 2, color: '#001A2E' },
    textShadow: { offsetX: 0, offsetY: 2, blur: 10, color: 'rgba(0, 80, 120, 0.9)' },
  },
  popline: {
    color: '#FF4D8D',
    fontWeight: 'bold',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    stroke: { width: 0, color: 'transparent' },
    textShadow: { offsetX: 0, offsetY: 0, blur: 0, color: 'transparent' },
  },
  beasty: {
    color: '#FFD700',
    fontWeight: 'bold',
    backgroundColor: 'transparent',
    stroke: { width: 3, color: '#000000' },
    textShadow: { offsetX: 2, offsetY: 2, blur: 0, color: '#000000' },
  },
  phantom: {
    color: '#E0E0E0',
    fontWeight: 'normal',
    backgroundColor: 'transparent',
    stroke: { width: 1, color: 'rgba(255, 255, 255, 0.4)' },
    textShadow: { offsetX: 0, offsetY: 0, blur: 12, color: 'rgba(255, 255, 255, 0.6)' },
  },
  galaxy: {
    color: '#B388FF',
    fontWeight: 'bold',
    backgroundColor: 'rgba(20, 10, 40, 0.6)',
    stroke: { width: 1, color: '#4A148C' },
    textShadow: { offsetX: 0, offsetY: 0, blur: 14, color: 'rgba(179, 136, 255, 0.8)' },
  },
  orange: {
    color: '#FF6D00',
    fontWeight: 'bold',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    stroke: { width: 2, color: '#000000' },
    textShadow: { offsetX: 0, offsetY: 2, blur: 6, color: 'rgba(0, 0, 0, 0.7)' },
  },
  glow: {
    color: '#FFFFFF',
    fontWeight: 'semibold',
    backgroundColor: 'transparent',
    stroke: { width: 0, color: 'transparent' },
    textShadow: { offsetX: 0, offsetY: 0, blur: 16, color: 'rgba(255, 255, 255, 0.9)' },
  },
  impact: {
    color: '#FFFFFF',
    fontWeight: 'bold',
    backgroundColor: '#000000',
    stroke: { width: 0, color: 'transparent' },
    textShadow: { offsetX: 0, offsetY: 0, blur: 0, color: 'transparent' },
  },
  vintage: {
    color: '#F5E6C8',
    fontWeight: 'normal',
    backgroundColor: 'rgba(40, 25, 10, 0.7)',
    stroke: { width: 1, color: '#8B6914' },
    textShadow: { offsetX: 1, offsetY: 1, blur: 2, color: 'rgba(0, 0, 0, 0.6)' },
  },
  mint: {
    color: '#00C896',
    fontWeight: 'semibold',
    backgroundColor: 'rgba(0, 40, 30, 0.5)',
    stroke: { width: 1, color: '#003D2B' },
    textShadow: { offsetX: 0, offsetY: 2, blur: 8, color: 'rgba(0, 0, 0, 0.5)' },
  },
  ember: {
    color: '#FF4500',
    fontWeight: 'bold',
    backgroundColor: 'transparent',
    stroke: { width: 2, color: '#1A0000' },
    textShadow: { offsetX: 0, offsetY: 0, blur: 12, color: 'rgba(255, 69, 0, 0.7)' },
  },
  lemon: {
    color: '#FFF700',
    fontWeight: 'bold',
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    stroke: { width: 2, color: '#000000' },
    textShadow: { offsetX: 0, offsetY: 2, blur: 6, color: 'rgba(0, 0, 0, 0.8)' },
  },
  crimson: {
    color: '#DC143C',
    fontWeight: 'bold',
    backgroundColor: 'transparent',
    stroke: { width: 2, color: '#000000' },
    textShadow: { offsetX: 1, offsetY: 1, blur: 4, color: 'rgba(0, 0, 0, 0.7)' },
  },
  glitch: {
    color: '#00FF41',
    fontWeight: 'bold',
    backgroundColor: 'transparent',
    stroke: { width: 1, color: '#FF00FF' },
    textShadow: { offsetX: 2, offsetY: 0, blur: 0, color: '#FF0000' },
  },
  hype: {
    color: '#FF1493',
    fontWeight: 'bold',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    stroke: { width: 2, color: '#FFFFFF' },
    textShadow: { offsetX: 2, offsetY: 2, blur: 0, color: '#000000' },
  },
  blur: {
    color: 'rgba(255, 255, 255, 0.85)',
    fontWeight: 'semibold',
    backgroundColor: 'transparent',
    stroke: { width: 0, color: 'transparent' },
    textShadow: { offsetX: 0, offsetY: 0, blur: 20, color: 'rgba(255, 255, 255, 0.5)' },
  },
}

/** 预编译的完整模板表（合并了默认值） */
export const clipCaptionStyleTemplates: Record<string, CaptionTextItemTemplate> =
  Object.fromEntries(
    Object.entries(partialTemplates).map(([key, partial]) => [
      key,
      { ...DEFAULT_CLIP_CAPTION_STYLE, ...partial },
    ]),
  )

/**
 * 根据字幕样式 key 获取对应的模板。
 * 未命中或未传 key 时返回默认样式。
 */
export function getClipCaptionTemplate(styleKey?: string): CaptionTextItemTemplate {
  if (styleKey && clipCaptionStyleTemplates[styleKey]) {
    return clipCaptionStyleTemplates[styleKey]
  }
  return DEFAULT_CLIP_CAPTION_STYLE
}
